import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * StatCard — one tile in the admin dashboard's stats strip. Each card is a
 * uppercase label (slate500) → big serif value → an optional colored delta
 * line per DESIGN.md Artboard 3 ("Stats Strip"):
 *
 *   Total books      Live             Pending review   Missing covers
 *   1,284            1,162            87               35
 *   +32 this week    94% of catalog   Needs review     +6 since Friday
 *                    (orange600)      (green700)       (amber)        (rose700)
 *
 * `tone` controls the delta color only — not the whole card — so the visual
 * weight stays on the value. The card is intentionally layout-agnostic
 * (parent grid decides column count), which is why it takes no width prop.
 */
export type StatCardTone = 'default' | 'success' | 'warning' | 'danger';

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  tone?: StatCardTone;
  className?: string;
}

/**
 * Map tone → delta text color. `default` keeps the brand orange for
 * neutral / positive-framed numbers (matches the "Total books" card in
 * the mock). `warning` falls through to tailwind's built-in amber because
 * the OR palette doesn't define an amber token yet — add one to
 * `styles/index.css` if this spreads beyond StatCard.
 */
const toneClass: Record<StatCardTone, string> = {
  default: 'text-or-orange-600',
  success: 'text-or-green-700',
  warning: 'text-amber-600',
  danger: 'text-or-rose-700',
};

export function StatCard({ label, value, delta, tone = 'default', className }: StatCardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-or-slate-200 bg-white px-5 py-4 shadow-sm',
        className,
      )}
    >
      <div className="or-kicker text-or-slate-500">{label}</div>
      <div className="mt-1 font-display text-[28px] font-semibold leading-tight text-or-slate-900">
        {value}
      </div>
      {delta ? (
        <div className={cn('mt-1 text-[12px] font-semibold', toneClass[tone])}>{delta}</div>
      ) : null}
    </div>
  );
}
